import { createAsyncThunk } from '@reduxjs/toolkit'

import callAPI from 'api'
import { Success, Fail } from 'store/types'
import { FETCHING_STATUS } from 'utils/constants'
import { CustomFieldLeadDetail } from 'types/global'

import {
  CreateLeadPayload,
  CreateLeadSourcePayload,
  CreateLeadSourceResponse,
  CreateLeadTaskPayload,
  GetLeadsPayload,
  GetLeadsResponse,
  GetLeadStatisticsResponse,
  CheckLeadExistsResponse,
  ReassignLeadOwnerPayload,
  ReassignLeadOwnerResponse,
  LeadAddressResponse,
  LeadDetailResponse,
  UpdateLeadInfoPayload,
  UpdateLeadProcessPayload,
  UpdateLeadStatusPayload,
  UpdateLeadAddressPayload,
  CreateLeadAddressPayload,
  AddAdditionalOwnerPayload,
  DeleteAdditionalOwnerPayload,
  GetLeadInvalidLostPayload,
  LeadInvalidLostResponse,
  UpdateInvalidLostReasonPayload,
  CreateLeadAppointmentPayload,
  CreateLeadAppointmentResponse,
  DeleteLeadAppointmentPayload,
  UpdateLeadAppointmentPayload,
  UpdateLeadAppointmentStatusPayload,
  GetLeadAppointmentResponse,
  GetLeadAppointmentPayload,
  LeadActivitiesResponse,
  GetLeadActivitiesPayload,
  GetLeadActivitiesInterface,
  UpdateLeadTaskPayload,
  RemoveLeadAppointmentPayload,
  LeadTasksResponse,
  GetLeadTasksPayload,
  UpdateLeadTaskResponse,
  UpdateLeadTaskStatusPayload,
  UpdateLeadTaskStatusResponse,
  GetLeadDocumentsPayload,
  GetLeadDocumentsResponse,
  CreateLeadNoteResponse,
  CreateLeadNotePayload,
  GetLeadTabCount,
  GetLeadTabCountResponse,
  UploadDocumentResponse,
  UploadDocumentPayload,
  GetPendingDocumentsPayload,
  GetPendingDocumentsResponse,
  PublishDocumentsPayload,
  AbortDocumentsPayload,
  GetLeadCallStatusesResponse,
  UpdateLeadNotePayload,
  DeleteLeadNotePayload,
  DeleteDocumentPayload,
  DownloadDocumentPayload,
  DownloadDocumentResponse,
  CreateCallLogPayload,
  CreateCallLogResponse,
  UpdateCallLogPayload,
  UpdateCallLogResponse,
  DeleteCallLogPayload,
  DeleteCallLogResponse,
  GetLeadCallbackDetailsResponse,
  GetLeadCallbackDetailsPayload,
  UpdateLeadAppointmentStatusResponse,
  CheckLeadDuplicatesV2Payload,
  CheckLeadDuplicatesV2Response,
  CreateLeadResponse,
  ExportLeadsDataResponse,
  GetNextLeadPayload,
  GetNextLeadResponse,
  CreateLeadSourceTypePayload,
  CreateLeadSourceTypeResponse,
  GetLeadSourceTypePayload,
  GetLeadSourceTypeResponse,
  UpdateLeadSourceTypePayload,
  UpdateLeadSourceTypeResponse,
  GetLeadSourcePayload,
  GetLeadSourceResponse,
  UpdateLeadSourcePayload,
  UpdateLeadSourceResponse,
  DeleteLeadSourcePayload,
  SetLeadSourceStatusPayload,
  BulkAssignLeadSourceCampaignPayload,
  BulkRecycleLeadsPayload,
  BulkRecycleLeadsResponse,
  UpdateLeadCustomFormValuePayload,
  ValidateMeetingLocationPayload,
  ValidateMeetingLocationResponse,
  StartMeetingPayload,
  EndMeetingPayload,
  StartMeetingResponse,
  GetMeetingStatus,
  BulkUpdateLeadStatus,
  GetHistoryLogResponse,
  GetHistoryLogPayload,
  MergeLeads,
  BulkUpdateLeadPriority,
  GetLeadActivityTypeResponse,
  UploadLeadCallLogAttachmentPayload,
  GetLeadCallLogAttachmentResponse,
  DeleteLeadCallLogAttachmentPayload,
  GetLeadCallLogAttachmentPayload,
  BulkDeleteLeadTasksPayload,
  BulkDeleteLeadTasksResponse,
  GetMergeableLeadsAPIPayload,
  MergeableLeadResponse,
  GetMergeableDuplicateLeadsAPIPayload,
  GetLeadDuplicateListResponse,
  GetLeadDuplicateListPayload,
  GetLeadFormConfigResponse,
  GetLeadSearchResponse,
  getLeadActivityTypesPayload,
  GetDefaultCallbackPayload,
  GetDefaultCallbackResponse,
} from './types/lead'

/**
 * Async function to create lead
 * Returns data directly
 */
export const createLead = async (payload: CreateLeadPayload) => {
  const response = await callAPI<CreateLeadResponse, CreateLeadPayload>('crm_create_lead', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })
  return response
}

/**
 * Async function to create lead source
 */
export const createLeadSourceApi = async (payload: CreateLeadSourcePayload) =>
  callAPI<CreateLeadSourceResponse, CreateLeadSourcePayload>('crm_create_lead_source', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const getLeadListApi = async (payload: GetLeadsPayload) =>
  await callAPI<GetLeadsResponse, GetLeadsPayload>('crm_get_leads', [payload], { requireAuthorization: true })

export const searchLeadListApi = async (payload: GetLeadsPayload) =>
  await callAPI<GetLeadSearchResponse, GetLeadsPayload>('crm_search_leads', [payload], {
    requireAuthorization: true,
  })

export const getMergeableDuplicateLeadListApi = async (payload: GetMergeableDuplicateLeadsAPIPayload) =>
  callAPI<CheckLeadDuplicatesV2Response, GetMergeableDuplicateLeadsAPIPayload>(
    'crm_get_mergeable_duplicate_leads',
    [payload],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

export const getMergeableLeadListApi = async (payload: GetMergeableLeadsAPIPayload) =>
  callAPI<MergeableLeadResponse, GetMergeableLeadsAPIPayload>('crm_get_mergeable_leads', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const getLeads = createAsyncThunk<Fail | Success<GetLeadsResponse>, GetLeadsPayload>(
  'lead/getLeads',
  getLeadListApi,
)

export const getLeadStatistics = async () =>
  await callAPI<GetLeadStatisticsResponse>('crm_get_lead_stats', undefined, { requireAuthorization: true })

export const checkLeadExistApi = async () =>
  await callAPI<CheckLeadExistsResponse>('crm_check_lead_exists', undefined, { requireAuthorization: true })

export const checkLeadExists = createAsyncThunk<Fail | Success<CheckLeadExistsResponse>>(
  'lead/checkLeadExists',
  checkLeadExistApi,
)

export const reassignLeadOwner = async (payload: ReassignLeadOwnerPayload) =>
  await callAPI<ReassignLeadOwnerResponse, ReassignLeadOwnerPayload>('crm_reassign_bulk_lead_owner', [payload], {
    requireAuthorization: true,
  })

export const getLeadAddresApi = async ({ leadId }: { leadId: string }) =>
  await callAPI<LeadAddressResponse>('crm_get_lead_addresses', [{ lead_id: leadId }], { requireAuthorization: true })

export const getLeadAddress = createAsyncThunk<Fail | Success<LeadAddressResponse>, { leadId: string }>(
  'lead/getLeadAddress',
  getLeadAddresApi,
)

export const getLeadLostReasonsApi = async (payload: GetLeadInvalidLostPayload) =>
  await callAPI<LeadInvalidLostResponse>('crm_get_lead_close_reasons', [payload], {
    requireAuthorization: true,
  })

export const getLeadLostReasons = createAsyncThunk<Fail | Success<LeadInvalidLostResponse>, GetLeadInvalidLostPayload>(
  'lead/getLostReasons',
  async (payload) =>
    await callAPI<LeadInvalidLostResponse>('crm_get_lead_close_reasons', [payload], {
      requireAuthorization: true,
    }),
)

export const getLeadInvalidReasons = createAsyncThunk<
  Fail | Success<LeadInvalidLostResponse>,
  GetLeadInvalidLostPayload
>(
  'lead/getInvalidReasons',
  async (payload) =>
    await callAPI<LeadInvalidLostResponse>('crm_get_lead_close_reasons', [payload], {
      requireAuthorization: true,
    }),
)

/**
 * Async function to update lost Reason
 */
export const updateLostReason = createAsyncThunk(
  'leads/updateInvalidLostReason',
  async ({ payload, callback }: UpdateInvalidLostReasonPayload) => {
    const response = await callAPI('crm_update_close_reasons', [payload], {
      requireAuthorization: true,
      reloadOnReauth: false,
    })

    if (response.status === 'SUCCESS' || response.status === 'FAIL') {
      if (typeof callback === 'function') {
        callback(response.status)
      }
    }
    return response
  },
)

export const getLeadDetailApi = async (leadId: string) =>
  await callAPI<LeadDetailResponse>('crm_get_lead_details', [{ lead_id: leadId }], {
    requireAuthorization: true,
  })

export const getLeadDetail = createAsyncThunk<Fail | Success<LeadDetailResponse>, string>(
  'lead/leadDetails',
  getLeadDetailApi,
)

const defaultFilter: Omit<GetLeadActivitiesPayload, 'lead_id'> = {
  filter: {
    view: [],
  },
  last_record_id: 100000000,
  per_page: 20,
  sort_by: { key: 'create_ts', order: 'desc' },
}

export const getLeadActivitiesApi = async (payload: GetLeadActivitiesInterface) => {
  const defaultPayload: GetLeadActivitiesPayload = {
    ...defaultFilter,
    ...payload,
  }

  return await callAPI<LeadActivitiesResponse>('crm_get_lead_activities', [defaultPayload], {
    requireAuthorization: true,
  })
}

export const getLeadActivities = createAsyncThunk<Fail | Success<LeadActivitiesResponse>, GetLeadActivitiesInterface>(
  'lead/leadActivities',
  getLeadActivitiesApi,
)

export const getLeadActivityTypes = async (payload?: getLeadActivityTypesPayload) =>
  await callAPI<GetLeadActivityTypeResponse>(
    'crm_get_lead_activity_types',
    [{ ...payload, excluded_activity_type: payload?.excluded_activity_type || [] }],
    {
      requireAuthorization: true,
    },
  )

export const removeLeadAppointment = async (payload: RemoveLeadAppointmentPayload) =>
  callAPI<{ success: boolean }, RemoveLeadAppointmentPayload>('crm_remove_lead_appointment', [payload], {
    requireAuthorization: true,
  })

export const getLeadTaskApi = async (payload: GetLeadTasksPayload) =>
  await callAPI<LeadTasksResponse>('crm_get_lead_tasks', [payload], {
    requireAuthorization: true,
  })

export const getLeadTaskApi_ = async (payload: GetLeadTasksPayload) =>
  await callAPI<LeadTasksResponse>('crm_get_task_event_list', [payload], {
    requireAuthorization: true,
  })

export const getLeadTasks = createAsyncThunk<Fail | Success<LeadTasksResponse>, GetLeadTasksPayload>(
  'lead/leadTasks',
  getLeadTaskApi,
)

export const updateLeadInfo = async (payload: UpdateLeadInfoPayload) =>
  await callAPI<{ success: boolean }>('crm_update_lead_info', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const updateLeadStatus = async (payload: UpdateLeadStatusPayload) =>
  await callAPI<{ success: boolean }, UpdateLeadStatusPayload>('crm_update_lead_stage', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const updateLeadProcess = async (payload: UpdateLeadProcessPayload) =>
  await callAPI<{ success: boolean }, UpdateLeadProcessPayload>('crm_update_lead_process', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const updateLeadAddress = async (payload: UpdateLeadAddressPayload) =>
  await callAPI<{ success: boolean }>('crm_update_lead_address', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const createLeadAddress = async (payload: CreateLeadAddressPayload) =>
  await callAPI<{ success: boolean }>('crm_create_lead_address', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const deleteLeadAddress = async (addressId: string) =>
  await callAPI<{ success: boolean }>('crm_delete_lead_address', [{ address_id: addressId }], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const setAddressStatus = async (addressId: string, setActive: boolean) =>
  await callAPI<{ success: boolean }>(
    'crm_set_lead_address_status',
    [{ address_id: addressId, is_active: setActive }],
    {
      requireAuthorization: true,
    },
  )

export const addAdditionalOwner = async (payload: AddAdditionalOwnerPayload) =>
  await callAPI<{ success: boolean; additionalOwners: Array<{ user_id: string; fname: string; lname: string }> }>(
    'crm_add_lead_additional_owner',
    [payload],
    {
      requireAuthorization: true,
    },
  )

export const deleteAdditionalOwner = async (payload: DeleteAdditionalOwnerPayload) =>
  await callAPI<{ success: boolean; additionalOwners: Array<{ user_id: string; fname: string; lname: string }> }>(
    'crm_delete_lead_additional_owner',
    [payload],
    { requireAuthorization: true, reloadOnReauth: false },
  )

/**
 * Lead Appointment
 */
export const createLeadAppointment = async (payload: CreateLeadAppointmentPayload) =>
  await callAPI<CreateLeadAppointmentResponse, CreateLeadAppointmentPayload>('crm_create_lead_appointment', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const deleteLeadAppointment = async (payload: DeleteLeadAppointmentPayload) =>
  callAPI<{ success: boolean }, DeleteLeadAppointmentPayload>('crm_delete_lead_appointment', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const getLeadAppointmentApi = async (payload: GetLeadAppointmentPayload) =>
  callAPI<GetLeadAppointmentResponse, GetLeadAppointmentPayload>('crm_get_lead_appointment', [payload], {
    requireAuthorization: true,
  })

export const getLeadAppointment = createAsyncThunk<
  Fail | Success<GetLeadAppointmentResponse>,
  GetLeadAppointmentPayload
>('lead/getLeadAppointment', getLeadAppointmentApi)

export const updateLeadAppointment = async (payload: UpdateLeadAppointmentPayload) =>
  callAPI<{ success: boolean }, UpdateLeadAppointmentPayload>('crm_update_lead_appointment', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export type UpdateLeadAppointmentConfirmationPayload = {
  lead_appointment_id: string
  is_confirmed: boolean
}
export const updateLeadAppointmentConfirmation = async (payload: UpdateLeadAppointmentConfirmationPayload) => {
  const response = await callAPI<{ success: boolean }, UpdateLeadAppointmentConfirmationPayload>(
    'crm_update_lead_appointment_confirmation',
    [payload],
    {
      requireAuthorization: true,
    },
  )

  return response
}

export const sendMeetingInvite = async (payload: { lead_appointment_id: string }) =>
  callAPI<{ success: boolean }, { lead_appointment_id: string }>('crm_send_meeting_invite', [payload], {
    requireAuthorization: true,
  })

export const updateLeadAppointmentStatus = createAsyncThunk<
  Fail | Success<UpdateLeadAppointmentStatusResponse>,
  UpdateLeadAppointmentStatusPayload
>('lead/updateAppointmentStatus', async (payload: UpdateLeadAppointmentStatusPayload) =>
  callAPI<UpdateLeadAppointmentStatusResponse>(
    'crm_update_lead_appointment_status',
    [
      {
        lead_appointment_id: payload.lead_appointment_id,
        lead_appointment_status_id: payload.lead_appointment_status_id,
      },
    ],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  ),
)

export const validateMeetingLocation = async (payload: ValidateMeetingLocationPayload) =>
  callAPI<ValidateMeetingLocationResponse, ValidateMeetingLocationPayload>('crm_validate_meeting_location', [payload], {
    requireAuthorization: true,
  })

export const startMeetingAppointment = async (payload: StartMeetingPayload) =>
  callAPI<StartMeetingResponse, StartMeetingPayload>('crm_start_meeting', [payload], {
    requireAuthorization: true,
  })

export const endMeetingAppointment = async (payload: EndMeetingPayload) =>
  callAPI<{ success: boolean }, EndMeetingPayload>('crm_end_meeting', [payload], {
    requireAuthorization: true,
  })

export const acceptPDPA = async (payload: { pdpa_id: number }) =>
  callAPI<{ success: boolean }>('crm_accept_pdpa', [payload], {
    requireAuthorization: true,
  })

export const getMeetingStatus = async () =>
  callAPI<GetMeetingStatus>('crm_get_meeting_in_progress', undefined, {
    requireAuthorization: true,
  })

export const getLeadCallStatusesApi = async () =>
  await callAPI<GetLeadCallStatusesResponse>('crm_get_call_status_data', undefined, { requireAuthorization: true })

export const getLeadCallbackDetails = async (payload: GetLeadCallbackDetailsPayload) => {
  const response = await callAPI<GetLeadCallbackDetailsResponse>('crm_get_callback_details', [payload], {
    requireAuthorization: true,
  })

  if (response.status !== FETCHING_STATUS.success) {
    return response
  }

  return {
    ...response,
    data: {
      callTime: response.data.call_time,
      contactInfoId: response.data.contact_info_id,
      isOtherNumber: response.data.is_other_number,
      otherContactInfoTypeId: response.data.other_contact_info_type_id,
      otherContactValue: response.data.other_contact_value,
    },
  }
}

/**
 * Lead Task
 */

export const createLeadTaskApi = async (payload: CreateLeadTaskPayload) =>
  await callAPI<UpdateLeadTaskResponse, CreateLeadTaskPayload['new_value']>(
    'crm_create_lead_task',
    [payload.new_value],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

export const createLeadTask = createAsyncThunk<Fail | Success<UpdateLeadTaskResponse>, CreateLeadTaskPayload>(
  'lead/createTask',
  createLeadTaskApi,
)

export const updateLeadTaskApi = async (payload: UpdateLeadTaskPayload) =>
  await callAPI<UpdateLeadTaskResponse, UpdateLeadTaskPayload['new_value']>(
    'crm_update_lead_task',
    [payload.new_value],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

export const updateLeadTask = createAsyncThunk<Fail | Success<UpdateLeadTaskResponse>, UpdateLeadTaskPayload>(
  'lead/updateTask',
  updateLeadTaskApi,
)

export const updateLeadTaskStatus = createAsyncThunk<
  Fail | Success<UpdateLeadTaskStatusResponse>,
  UpdateLeadTaskStatusPayload
>('lead/updateLeadTaskStatus', async ({ task_id, is_completed, skipRequest }) => {
  if (skipRequest) {
    return new Promise((resolve) => {
      resolve({ data: { data: { task_id }, success: true }, status: FETCHING_STATUS.success })
    })
  }

  return updateLeadTaskStatusApi({ task_id, is_completed, skipRequest })
})

export const updateLeadTaskStatusApi = async (payload: UpdateLeadTaskStatusPayload) =>
  await callAPI<UpdateLeadTaskStatusResponse>('crm_update_lead_task_status', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const deleteLeadTask = async (taskId: string) =>
  await callAPI<{ success: boolean }>('crm_delete_lead_task', [{ lead_task_id: taskId }], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const bulkDeleteLeadTasks = async (payload: BulkDeleteLeadTasksPayload) =>
  await callAPI<BulkDeleteLeadTasksResponse, BulkDeleteLeadTasksPayload>('crm_bulk_delete_tasks', [payload], {
    requireAuthorization: true,
  })

export const getDefaultCallback = async (payload: GetDefaultCallbackPayload) =>
  await callAPI<GetDefaultCallbackResponse, GetDefaultCallbackPayload>('crm_get_default_callback', [payload], {
    requireAuthorization: true,
  })

export const getLeadDocuments = createAsyncThunk<Fail | Success<GetLeadDocumentsResponse>, GetLeadDocumentsPayload>(
  'lead/getLeadDocuments',
  async (payload) =>
    await callAPI<GetLeadDocumentsResponse>('crm_get_lead_documents', [payload], {
      requireAuthorization: true,
    }),
)

export const getLeadTabCount = async (payload: GetLeadTabCount) =>
  callAPI<GetLeadTabCountResponse, GetLeadTabCount>('crm_get_lead_tab_count', [payload], {
    requireAuthorization: true,
  })

export const getLeadTabCountV2 = async (payload: GetLeadTabCount) =>
  callAPI<GetLeadTabCountResponse, GetLeadTabCount>('crm_get_lead_tab_count_v2', [payload], {
    requireAuthorization: true,
  })

/**
 * Lead Notes
 */
export const createLeadNote = createAsyncThunk<Fail | Success<CreateLeadNoteResponse>, CreateLeadNotePayload>(
  'lead/createLeadNote',
  async (payload: CreateLeadNotePayload) =>
    await callAPI<CreateLeadNoteResponse>(
      'crm_create_lead_note',
      [
        {
          lead_id: payload.lead_id,
          lead_note: payload.lead_note,
        },
      ],
      { requireAuthorization: true, reloadOnReauth: false },
    ),
)

export const deleteLeadNote = createAsyncThunk<Fail | Success<{ success: boolean }>, DeleteLeadNotePayload>(
  'lead/deleteLeadNote',
  async (payload: DeleteLeadNotePayload) =>
    await callAPI<{ success: boolean }>('crm_delete_lead_note', [{ note_id: payload.lead_note_id }], {
      requireAuthorization: true,
      reloadOnReauth: false,
    }),
)

export const updateLeadNote = createAsyncThunk<Fail | Success<{ success: boolean }>, UpdateLeadNotePayload>(
  'lead/updateLeadNote',
  async (payload: UpdateLeadNotePayload) =>
    await callAPI<{ success: boolean }>(
      'crm_update_lead_note',
      [{ lead_note_id: payload.lead_note_id, note: payload.note }],
      { requireAuthorization: true, reloadOnReauth: false },
    ),
)

/**
 * Lead Documents
 */
export const uploadDocument = createAsyncThunk<Fail | Success<UploadDocumentResponse>, UploadDocumentPayload>(
  'lead/uploadDocument',
  async (payload) =>
    await callAPI<UploadDocumentResponse>('crm_upload_lead_document', [payload], {
      requireAuthorization: true,
    }),
)

export const getPendingDocuments = createAsyncThunk<
  Fail | Success<GetPendingDocumentsResponse>,
  GetPendingDocumentsPayload
>(
  'lead/getPendingDocuments',
  async (payload) =>
    await callAPI<GetPendingDocumentsResponse>('crm_get_lead_pending_documents', [payload], {
      requireAuthorization: true,
    }),
)

export const publishDocuments = async (payload: PublishDocumentsPayload) =>
  callAPI<{ success: boolean }, PublishDocumentsPayload>('crm_publish_lead_documents', [payload], {
    requireAuthorization: true,
  })

export const abortDocuments = async (payload: AbortDocumentsPayload) =>
  callAPI<{ success: boolean }, AbortDocumentsPayload>('crm_abort_document_upload', [payload], {
    requireAuthorization: true,
  })

export const deleteDocument = async (payload: DeleteDocumentPayload) =>
  callAPI<{ success: boolean }, DeleteDocumentPayload>('crm_delete_lead_document', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const downloadDocument = createAsyncThunk<Fail | Success<DownloadDocumentResponse>, DownloadDocumentPayload>(
  'lead/downloadDocument',
  async (payload: DownloadDocumentPayload) =>
    callAPI<DownloadDocumentResponse>('crm_download_document', [{ document_id: payload.document_id }], {
      requireAuthorization: true,
    }),
)

//Lead Call Log
export const createCallLog = createAsyncThunk<Fail | Success<CreateCallLogResponse>, CreateCallLogPayload>(
  'lead/createCallLog',
  async ({ onFail: _onFail, onSuccess: _onSuccess, ...payload }: CreateCallLogPayload) =>
    await callAPI('crm_create_lead_call_log', [payload], { requireAuthorization: true, reloadOnReauth: false }),
)

export const updateCallLog = createAsyncThunk<Fail | Success<UpdateCallLogResponse>, UpdateCallLogPayload>(
  'lead/updateCallLog',
  async ({ onFail: _onFail, onSuccess: _onSuccess, ...payload }: UpdateCallLogPayload) =>
    await callAPI('crm_update_lead_call_log', [payload], { requireAuthorization: true, reloadOnReauth: false }),
)

export const deleteCallLog = createAsyncThunk<Fail | Success<DeleteCallLogResponse>, DeleteCallLogPayload>(
  'lead/deleteCallLog',
  async (payload: DeleteCallLogPayload) =>
    await callAPI('crm_delete_lead_call_log', [payload], { requireAuthorization: true, reloadOnReauth: false }),
)

export const uploadLeadCallLogAttachment = async (payload: UploadLeadCallLogAttachmentPayload) =>
  await callAPI<GetLeadCallLogAttachmentResponse, UploadLeadCallLogAttachmentPayload>(
    'crm_upload_lead_call_log_attachment',
    [payload],
    {
      requireAuthorization: true,
    },
  )

export const deleteLeadCallLogAttachment = async (paylod: DeleteLeadCallLogAttachmentPayload) =>
  await callAPI<GetLeadCallLogAttachmentResponse, DeleteLeadCallLogAttachmentPayload>(
    'crm_delete_lead_call_log_attachment',
    [paylod],
    {
      requireAuthorization: true,
    },
  )

export const getLeadCallLogAttachment = async (paylod: GetLeadCallLogAttachmentPayload) =>
  await callAPI<GetLeadCallLogAttachmentResponse, GetLeadCallLogAttachmentPayload>(
    'crm_get_lead_call_log_attachment',
    [paylod],
    {
      requireAuthorization: true,
    },
  )

//Others

export const checkLeadDuplicatesV2 = async (payload: CheckLeadDuplicatesV2Payload) =>
  callAPI<CheckLeadDuplicatesV2Response, CheckLeadDuplicatesV2Payload>('crm_check_lead_duplicates_v2', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const getLeadDuplicatesList = async (payload: GetLeadDuplicateListPayload) =>
  await callAPI<GetLeadDuplicateListResponse, GetLeadDuplicateListPayload>('crm_get_lead_duplicates_list', [payload], {
    requireAuthorization: true,
  })

export type GetLeadDuplicatesCountResponse = {
  type: 'exact' | 'potential'
  total: number
  exacts_count: number
  potentials_count: number
}

export const getLeadDuplicatesCount = async (payload: string) =>
  await callAPI<GetLeadDuplicatesCountResponse>('crm_get_lead_duplicates_count', [{ lead_id: payload }], {
    requireAuthorization: true,
  })

export const exportLeadDataApi = async (payload: GetLeadsPayload) =>
  await callAPI<ExportLeadsDataResponse, GetLeadsPayload>('crm_download_excel', [payload], {
    requireAuthorization: true,
  })

export const exportLeadData = createAsyncThunk<Fail | Success<ExportLeadsDataResponse>, GetLeadsPayload>(
  'lead/exportLeadsData',
  exportLeadDataApi,
)

export const exportLeadContactsApi = async (payload: GetLeadsPayload) =>
  await callAPI<ExportLeadsDataResponse, GetLeadsPayload>('crm_export_lead_contacts', [payload], {
    requireAuthorization: true,
  })

export const exportLeadAddressesApi = async (payload: GetLeadsPayload) =>
  await callAPI<ExportLeadsDataResponse, GetLeadsPayload>('crm_export_lead_addresses', [payload], {
    requireAuthorization: true,
  })

export const exportMeetingsAndCallLogsApi = async (payload: { start_date: string; end_date: string }) =>
  await callAPI<ExportLeadsDataResponse, { start_date: string; end_date: string }>(
    'crm_download_meetings_call_logs',
    [payload],
    { requireAuthorization: true },
  )

export const exportLeadActivitiesApi = async (payload: GetLeadsPayload & { start_date: string; end_date: string }) =>
  await callAPI<ExportLeadsDataResponse, GetLeadsPayload & { start_date: string; end_date: string }>(
    'crm_download_lead_activities',
    [payload],
    {
      requireAuthorization: true,
    },
  )
/**
 * Get Next Lead
 */
export const getNextLead = async (payload: GetNextLeadPayload) =>
  await callAPI<GetNextLeadResponse, GetNextLeadPayload>('crm_get_next_task', [payload], {
    requireAuthorization: true,
  })

/**
 * Lead Source Type
 */
export const createLeadSourceType = async (payload: CreateLeadSourceTypePayload) =>
  callAPI<{ success: boolean } & CreateLeadSourceTypeResponse, CreateLeadSourceTypePayload>(
    'crm_create_lead_source_type',
    [payload],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

export const getLeadSourceType = async (payload: GetLeadSourceTypePayload) =>
  callAPI<{ success: boolean } & GetLeadSourceTypeResponse, GetLeadSourceTypePayload>(
    'crm_get_lead_source_type',
    [payload],
    {
      requireAuthorization: true,
    },
  )

export const updateLeadSourceType = async (payload: UpdateLeadSourceTypePayload) =>
  callAPI<{ success: boolean } & UpdateLeadSourceTypeResponse, UpdateLeadSourceTypePayload>(
    'crm_update_lead_source_type',
    [payload],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

export const getLeadSource = async (payload: GetLeadSourcePayload) =>
  callAPI<{ success: boolean } & GetLeadSourceResponse, GetLeadSourcePayload>('crm_get_lead_source', [payload], {
    requireAuthorization: true,
  })

export const updateLeadSource = async (payload: UpdateLeadSourcePayload) =>
  callAPI<{ success: boolean } & UpdateLeadSourceResponse, UpdateLeadSourcePayload>(
    'crm_update_lead_source',
    [payload],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

export const deleteLeadSource = async (payload: DeleteLeadSourcePayload) =>
  callAPI<{ success: boolean }, DeleteLeadSourcePayload>('crm_delete_lead_source', [payload], {
    requireAuthorization: true,
    reloadOnReauth: false,
  })

export const setLeadSourceStatus = async (payload: SetLeadSourceStatusPayload) =>
  callAPI<{ success: boolean }, SetLeadSourceStatusPayload>('crm_set_lead_source_status', [payload], {
    requireAuthorization: true,
  })

/**
 * Bulk Actions
 */
export const bulkAssignLeadSourceCampaign = async (payload: BulkAssignLeadSourceCampaignPayload) =>
  callAPI<{ success: boolean }, BulkAssignLeadSourceCampaignPayload>('crm_reassign_bulk_source_campaign', [payload], {
    requireAuthorization: true,
  })

export const getCommonLeadStatuses = async (payload: BulkUpdateLeadStatus.FetchOptionsPayload) =>
  await callAPI<BulkUpdateLeadStatus.FetchOptionsResponse, BulkUpdateLeadStatus.FetchOptionsPayload>(
    'crm_get_common_lead_status',
    [payload],
    {
      requireAuthorization: true,
    },
  )

export const bulkUpdateLeadStatus = async (payload: BulkUpdateLeadStatus.Payload) =>
  await callAPI<BulkUpdateLeadStatus.Response, BulkUpdateLeadStatus.Payload>('crm_update_bulk_lead_status', [payload], {
    requireAuthorization: true,
  })

export const mergeLeads = async (payload: MergeLeads.Payload) =>
  await callAPI<MergeLeads.Response, MergeLeads.Payload>('crm_merged_lead', [payload], { requireAuthorization: true })

export const bulkUpdateLeadPriority = async (payload: BulkUpdateLeadPriority.Payload) =>
  await callAPI<BulkUpdateLeadPriority.Response, BulkUpdateLeadPriority.Payload>(
    'crm_bulk_update_lead_priority',
    [payload],
    { requireAuthorization: true },
  )

/**
 * Recycle Lead
 */
export const bulkRecycleLeads = async (payload: BulkRecycleLeadsPayload) =>
  await callAPI<BulkRecycleLeadsResponse, BulkRecycleLeadsPayload>('crm_bulk_recycle_leads', [payload], {
    requireAuthorization: true,
  })

/**
 * Custom Fields
 */
export const updateLeadCustomFormValue = async (payload: UpdateLeadCustomFormValuePayload) =>
  callAPI<{ success: boolean; data: CustomFieldLeadDetail[] }, UpdateLeadCustomFormValuePayload>(
    'crm_update_lead_custom_field_value',
    [payload],
    {
      requireAuthorization: true,
      reloadOnReauth: false,
    },
  )

/**
 * History Log
 */
export const getHistoryLog = async (payload: GetHistoryLogPayload) =>
  await callAPI<GetHistoryLogResponse, GetHistoryLogPayload>('crm_get_audit_logs', [payload], {
    requireAuthorization: true,
  })

/**
 * Delete and restore lead
 */
export const bulkDeleteLead = async (payload: { lead_ids: string[] }) =>
  await callAPI<{ success: boolean; warningMessage?: string }, { lead_ids: string[] }>(
    'crm_bulk_lead_delete',
    [payload],
    {
      requireAuthorization: true,
    },
  )

export const bulkRestoreLead = async (payload: { lead_ids: string[] }) =>
  await callAPI<{ success: boolean; warningMessage?: string }, { lead_ids: string[] }>(
    'crm_bulk_lead_restore',
    [payload],
    {
      requireAuthorization: true,
    },
  )

export const getLeadFormConfig = async () =>
  await callAPI<GetLeadFormConfigResponse>('crm_get_lead_form_config', undefined, {
    requireAuthorization: true,
  })

export default { createLead, getLeadStatistics, getLeadFormConfig }
